'use client';

import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { sendGTMEvent } from '@next/third-parties/google';
import { useActiveTime } from './useActiveTime';
import { POSTHOG_EVENTS } from '@/lib/posthog';

const HIGH_ENGAGEMENT_THRESHOLD = 60;

const THROTTLE_MS = 2_000;
const MIN_ACTIVE_TIME_MS = 2_000; // Drop visits under 2s
const TIME_CAP_SECONDS = 120;
const CLICK_CAP = 5;

function normalize(value: number, cap: number): number {
  return Math.min(Math.log(1 + value) / Math.log(1 + cap), 1) * 100;
}

function computeScore(activeTimeSec: number, scrollPercent: number, clicks: number): number {
  const timeScore = normalize(activeTimeSec, TIME_CAP_SECONDS);
  const scrollScore = Math.min(scrollPercent, 100);
  const clickScore = normalize(clicks, CLICK_CAP);

  const score = 0.4 * timeScore + 0.35 * scrollScore + 0.25 * clickScore;
  return Math.round(score);
}

export function useEngagementScore() {
  const posthog = usePostHog();
  const pathname = usePathname();
  const { getActiveTimeMs, reset: resetActiveTime } = useActiveTime();

  const maxScrollRef = useRef(0);
  const clickCountRef = useRef(0);
  const hasFiredRef = useRef(false);
  const lastScrollCheckRef = useRef(0);
  const pathnameRef = useRef(pathname);

  const fireScore = useCallback(() => {
    if (hasFiredRef.current || !posthog) return;

    const activeTimeMs = getActiveTimeMs();
    if (activeTimeMs < MIN_ACTIVE_TIME_MS) return;

    hasFiredRef.current = true;

    const activeTimeSec = activeTimeMs / 1000;
    const scrollPercent = maxScrollRef.current;
    const clicks = clickCountRef.current;

    const timeScore = Math.round(normalize(activeTimeSec, TIME_CAP_SECONDS));
    const scrollScore = Math.round(Math.min(scrollPercent, 100));
    const clickScore = Math.round(normalize(clicks, CLICK_CAP));
    const score = computeScore(activeTimeSec, scrollPercent, clicks);

    posthog.capture(POSTHOG_EVENTS.ENGAGEMENT_SCORE, {
      score,
      time_score: timeScore,
      scroll_score: scrollScore,
      click_score: clickScore,
      active_time_seconds: Math.round(activeTimeSec),
      max_scroll_percent: Math.round(scrollPercent),
      click_count: clicks,
      page_path: pathnameRef.current,
    });

    if (score > HIGH_ENGAGEMENT_THRESHOLD) {
      sendGTMEvent({
        event: POSTHOG_EVENTS.ENGAGEMENT_SCORE,
        score,
        page_path: pathnameRef.current,
      });
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[Engagement] Score fired:', {
        score,
        timeScore,
        scrollScore,
        clickScore,
        activeTimeSec: Math.round(activeTimeSec),
        scrollPercent: Math.round(scrollPercent),
        clicks,
        path: pathnameRef.current,
      });
    }
  }, [posthog, getActiveTimeMs]);

  const resetAll = useCallback(() => {
    maxScrollRef.current = 0;
    clickCountRef.current = 0;
    hasFiredRef.current = false;
    lastScrollCheckRef.current = 0;
    resetActiveTime();
  }, [resetActiveTime]);

  // Fire on page navigation (client-side)
  useEffect(() => {
    if (pathnameRef.current !== pathname) {
      fireScore();
      pathnameRef.current = pathname;
      resetAll();
    }
  }, [pathname, fireScore, resetAll]);

  // Scroll + click tracking, and fire on visibility/unload
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollCheckRef.current < THROTTLE_MS) return;
      lastScrollCheckRef.current = now;

      const scrollTop = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      if (documentHeight <= windowHeight) return;

      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;
      if (scrollPercent > maxScrollRef.current) {
        maxScrollRef.current = scrollPercent;
      }
    };

    const handleClick = () => {
      clickCountRef.current += 1;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        fireScore();
      }
    };

    const handleBeforeUnload = () => {
      fireScore();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleClick, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [fireScore]);
}
